// The testnet hook card: /api/data/hook when the worker has a readout, else a direct read of BellHook and
// BellConfig on testnet 46630 for the TSLA/mUSDG pool.
import type { HookPayload } from '../api/types';
import { encAddr, poolKeyData, wordAddr, wordBig } from './abi';
import { SEL, T_CONFIG, T_HOOK, T_POOL, TESTNET } from './chain';
import { readFeed, type FeedReading } from './feed';
import { parseTs } from './format';
import { callWord, callWords } from './rpc';

/** BellHook._tierBaseFee (hook-v2): tickSpacing * 50 fee units (0.5 bps per unit of spacing), clamped to
 *  [100, 100,000] units (1 to 1,000 bps). Spacings 1, 10, 60, 200 give the standard 1, 5, 30, 100 bps
 *  tiers. Without a known positive spacing the fallback (Curve.baseFee) applies. */
export const TIER_UNITS_PER_SPACING = 50;
export const TIER_MIN_FEE = 100;
export const TIER_MAX_FEE = 100_000;
export function tierBaseFee(tickSpacing: number | null | undefined, fallbackFee: number): number {
  if (typeof tickSpacing !== 'number' || !Number.isInteger(tickSpacing) || tickSpacing <= 0) return fallbackFee;
  return Math.min(Math.max(tickSpacing * TIER_UNITS_PER_SPACING, TIER_MIN_FEE), TIER_MAX_FEE);
}

/** The fee a fresh feed charges. A hook-v2 config (its curve() reports deadbandBps) uses the pool's tier;
 *  the 2026-09-08 testnet hook predates tiers and charges curve.baseFee on every pool. */
export function effectiveBase(baseFee: number, tickSpacing: number | null | undefined, v2: boolean): number {
  return v2 ? tierBaseFee(tickSpacing, baseFee) : baseFee;
}

export interface HookReading {
  fee: number;
  gapBps: number;
  floatBps: number;
  stale: boolean;
  /** Curve.baseFee in BellConfig: the base itself before hook-v2, the fallback for other spacings after. */
  baseFee: number;
  /** The base the pool charges while the feed is fresh (effectiveBase). */
  base: number;
  tickSpacing: number | null;
  slope: number;
  maxFee: number;
  staleAfter: number;
  /** Float guard threshold in bps of the token's on-chain supply. */
  guardBps: number;
  /** Float guard surcharge in fee units (the API's float_guard.extraFee). */
  guardFee: number;
  /** Hook address: the worker's readout when it answers, else T_HOOK. */
  hook: string;
  stock: string;
  quote: string;
  feed: string;
  feedRead: FeedReading | null;
}

/** /api/data/hook to the shape of the direct read. The surcharge is float_guard.extraFee, the threshold
 *  float_guard.thresholdBps; a missing required number rejects the payload so the card falls back. */
export function hookFromApi(j: HookPayload): HookReading {
  const num = (v: unknown, name: string): number => {
    if (typeof v !== 'number' || !Number.isFinite(v)) throw new Error(`hook payload lacks ${name}`);
    return v;
  };
  const feedAt = parseTs(j.feed.updated_at);
  const baseFee = num(j.curve.baseFee, 'curve.baseFee');
  const tickSpacing = typeof j.pool_key.tickSpacing === 'number' ? j.pool_key.tickSpacing : null;
  return {
    fee: num(j.fee, 'fee'),
    gapBps: num(j.gap_bps, 'gap_bps'),
    floatBps: num(j.float_share_bps, 'float_share_bps'),
    stale: j.is_stale,
    baseFee,
    base: effectiveBase(baseFee, tickSpacing, typeof j.curve.deadbandBps === 'number'),
    tickSpacing,
    slope: num(j.curve.slope, 'curve.slope'),
    maxFee: num(j.curve.maxFee, 'curve.maxFee'),
    staleAfter: num(j.curve.staleAfter, 'curve.staleAfter'),
    guardBps: num(j.float_guard.thresholdBps, 'float_guard.thresholdBps'),
    guardFee: num(j.float_guard.extraFee, 'float_guard.extraFee'),
    hook: j.hook || T_HOOK,
    // The fallbacks assume the stock is currency1: true for the 2026-09-23 pool (mUSDG currency0, TSLA
    // currency1, see T_POOL in chain.ts). Swap them if a redeploy changes the sort order.
    stock: j.pool_info.stock || T_POOL.currency1,
    quote: j.pool_info.quote || T_POOL.currency0,
    feed: j.pool_info.feed || '',
    feedRead: typeof j.feed.price === 'number' && feedAt ? { price: j.feed.price, updatedAt: feedAt } : null,
  };
}

export async function readHook(): Promise<HookReading> {
  const key = poolKeyData(T_POOL);
  const [feeRaw, gapRaw, floatRaw, staleRaw, curveRaw, guardRaw, infoRaw] = await Promise.all([
    callWord(T_HOOK, SEL.getFee + key, TESTNET),
    callWord(T_HOOK, SEL.gapBps + key, TESTNET),
    callWord(T_HOOK, SEL.floatShareBps + key, TESTNET),
    // isStale takes the stock token; TSLA is currency1 of T_POOL (mUSDG currency0), checked 2026-09-23.
    callWord(T_HOOK, SEL.isStale + encAddr(T_POOL.currency1), TESTNET),
    callWords(T_CONFIG, SEL.curve, 4, TESTNET),
    callWords(T_CONFIG, SEL.floatGuard, 2, TESTNET),
    callWords(T_HOOK, SEL.poolInfo + T_POOL.id.slice(2), 6, TESTNET),
  ]);
  const c = curveRaw.slice(2), g = guardRaw.slice(2), i = infoRaw.slice(2);
  const baseFee = Number(wordBig(c, 0));
  const v2 = c.length >= 64 * 6; // curve(): 4 words (2026-09-08) or 6 words (hook-v2, staleFloor and deadbandBps)
  const out: HookReading = {
    fee: Number(wordBig(feeRaw.slice(2), 0)),
    gapBps: Number(wordBig(gapRaw.slice(2), 0)),
    floatBps: Number(wordBig(floatRaw.slice(2), 0)),
    stale: wordBig(staleRaw.slice(2), 0) === 1n,
    baseFee, base: effectiveBase(baseFee, T_POOL.tickSpacing, v2), tickSpacing: T_POOL.tickSpacing, slope: Number(wordBig(c, 1)), maxFee: Number(wordBig(c, 2)), staleAfter: Number(wordBig(c, 3)),
    guardBps: Number(wordBig(g, 0)), guardFee: Number(wordBig(g, 1)),
    hook: T_HOOK,
    stock: wordAddr(i, 0), quote: wordAddr(i, 1), feed: wordAddr(i, 2),
    feedRead: null,
  };
  try { out.feedRead = await readFeed(out.feed, TESTNET); } catch { out.feedRead = null; }
  return out;
}
